import { Check, Clock, Sparkles, Shield, Zap } from "lucide-react";
import { useEffect, useState } from "react";

const HeroSection = ({ navigate }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-[90vh] sm:min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950">
      {/* Simplified background - fewer layers for iOS performance */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-[500px] h-[500px] bg-blue-500/30 rounded-full blur-3xl -top-48 -left-48 animate-pulse-glow hidden sm:block"></div>
        <div className="absolute w-[500px] h-[500px] bg-indigo-500/30 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse-glow hidden sm:block" style={{ animationDelay: '1s' }}></div>
        {/* Mobile: lighter blurs */}
        <div className="absolute w-64 h-64 bg-blue-500/20 rounded-full blur-2xl -top-20 -left-20 sm:hidden"></div>
        <div className="absolute w-64 h-64 bg-indigo-500/20 rounded-full blur-2xl -bottom-20 -right-20 sm:hidden"></div>
      </div>

      {/* Tech grid - lightweight */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.1)_1px,transparent_1px)] bg-[size:50px_50px] animate-grid-flow"></div>
      </div>

      {/* Gradient waves - desktop only (heavy on mobile GPU) */}
      <div className="absolute inset-0 overflow-hidden opacity-40 hidden sm:block">
        <div className="absolute h-2 w-full bg-gradient-to-r from-transparent via-blue-400 to-transparent top-1/4 blur-sm animate-wave-fast"></div>
        <div className="absolute h-2 w-full bg-gradient-to-r from-transparent via-indigo-400 to-transparent top-1/2 blur-sm animate-wave-medium" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute h-2 w-full bg-gradient-to-r from-transparent via-cyan-400 to-transparent top-3/4 blur-sm animate-wave-slow" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Floating orbs - desktop only */}
      <div className="absolute inset-0 overflow-hidden hidden sm:block">
        <div className="absolute w-40 h-40 bg-blue-400/20 rounded-full blur-xl top-1/4 left-1/4 animate-float-orbit shadow-[0_0_50px_rgba(59,130,246,0.5)]"></div>
        <div className="absolute w-48 h-48 bg-indigo-400/20 rounded-full blur-xl bottom-1/4 right-1/4 animate-float-orbit shadow-[0_0_60px_rgba(99,102,241,0.5)]" style={{ animationDelay: '1.5s' }}></div>
      </div>

      {/* Pulsing circles - desktop only */}
      <div className="absolute inset-0 overflow-hidden opacity-20 hidden sm:block">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] border-2 border-blue-400 rounded-full animate-ripple"></div>
        <div className="absolute bottom-0 left-0 w-[700px] h-[700px] border-2 border-cyan-400 rounded-full animate-ripple" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Particles - desktop only */}
      <div className="absolute inset-0 opacity-60 hidden sm:block">
        <div className="absolute top-20 left-20 w-3 h-3 bg-blue-400 rounded-full animate-pulse-bright shadow-[0_0_10px_rgba(59,130,246,0.8)]"></div>
        <div className="absolute top-40 right-32 w-3 h-3 bg-indigo-400 rounded-full animate-pulse-bright shadow-[0_0_10px_rgba(99,102,241,0.8)]" style={{ animationDelay: '0.3s' }}></div>
        <div className="absolute bottom-32 left-40 w-3 h-3 bg-cyan-400 rounded-full animate-pulse-bright shadow-[0_0_10px_rgba(34,211,238,0.8)]" style={{ animationDelay: '0.6s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-sky-400 rounded-full animate-pulse-bright shadow-[0_0_10px_rgba(56,189,248,0.8)]" style={{ animationDelay: '0.9s' }}></div>
      </div>

      {/* Scanlines - desktop only */}
      <div className="absolute inset-0 pointer-events-none opacity-30 hidden sm:block">
        <div className="absolute w-full h-px bg-gradient-to-r from-transparent via-white/30 to-transparent animate-scan-fast"></div>
      </div>

      {/* Rotating gradient - desktop only */}
      <div className="absolute inset-0 opacity-20 hidden sm:block">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-indigo-500/30 to-cyan-500/0 animate-rotate-gradient"></div>
      </div>

      {/* Star field - desktop only */}
      <div className="absolute inset-0 opacity-40 hidden sm:block">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-twinkle"
            style={{
              top: `${(i * 5 + 3) % 100}%`,
              left: `${(i * 7 + 11) % 100}%`,
              animationDelay: `${(i * 0.15) % 3}s`
            }}
          ></div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left side - Content */}
          <div className={`text-white space-y-6 sm:space-y-8 transition-all duration-1000 transform ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-12 opacity-0'
          }`}>
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/20">
              <span className="text-xs sm:text-sm font-medium">Plataforma #1 en México</span>
            </div>

            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-medium leading-tight">
              <span 
                className="bg-gradient-to-r from-blue-300 via-white to-cyan-300 bg-clip-text text-transparent drop-shadow-[0_2px_10px_rgba(59,130,246,0.5)] animate-gradient-flow" 
                style={{ 
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  WebkitTextStroke: '0.5px rgba(255,255,255,0.3)', 
                  backgroundSize: '200% 200%' 
                }}
              >
                Gestiona tus Trámites
              </span>
              <br />
              <span className="text-3xl sm:text-4xl md:text-5xl text-white/90 font-normal">Fácil, Rápido y Seguro</span>
            </h1>

            <p className="text-base sm:text-xl md:text-2xl text-gray-200 leading-relaxed">
              Plataforma integral para trámites mexicanos con seguimiento en tiempo real 24/7
            </p>

            <div className="hidden sm:flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                data-testid="hero-search-btn"
                onClick={() => navigate('/categorias')}
                className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-bold text-base sm:text-lg overflow-hidden transform hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-purple-500/50"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <Zap className="w-5 h-5" />
                  Buscar Mi Trámite
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              <button
                data-testid="hero-quote-btn"
                onClick={() => navigate('/register')}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white rounded-xl font-bold text-base sm:text-lg hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
              >
                Cotizar Ahora
              </button>
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-4">
              {[
                { icon: Check, text: "100% Seguro" },
                { icon: Clock, text: "Seguimiento 24/7" },
                { icon: Shield, text: "Gestores Certificados" }
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center space-x-1 sm:space-x-2 bg-white/10 backdrop-blur-md rounded-full px-2 sm:px-4 py-1 sm:py-2 border border-white/20 hover:bg-white/20 transition-all duration-300 whitespace-nowrap"
                >
                  <item.icon className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 flex-shrink-0" />
                  <span className="font-medium text-[10px] sm:text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Video */}
          <div className={`relative transition-all duration-1000 transform delay-300 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-12 opacity-0'
          }`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/20 backdrop-blur-sm bg-white/5">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-[90%] h-auto rounded-2xl mx-auto"
                poster="/images/hero-poster.jpg"
              >
                <source src="/hero-video.mp4" type="video/mp4" />
                <img 
                  src="/images/hero-poster.jpg" 
                  alt="Oficina profesional"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </video>
              
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 via-transparent to-purple-600/20 pointer-events-none"></div>
            </div>

            {/* Stats */}
            <div className="flex gap-3 mt-4 sm:mt-6">
              <div className="flex-1 bg-white rounded-xl p-3 sm:p-4 shadow-lg border border-gray-100">
                <div className="text-2xl sm:text-3xl font-bold text-blue-600">10K+</div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">Trámites Exitosos</div>
              </div>
              <div className="flex-1 bg-white rounded-xl p-3 sm:p-4 shadow-lg border border-gray-100">
                <div className="text-2xl sm:text-3xl font-bold text-purple-600">95%</div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">Satisfacción</div>
              </div>
            </div>

            {/* Decorative - desktop only */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full opacity-20 blur-2xl animate-float hidden sm:block"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-br from-green-400 to-blue-500 rounded-full opacity-20 blur-2xl animate-float hidden sm:block" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-[-1px] left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
